import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, MagnifyingGlass } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { apiService, getImageUrl } from '../../config/api';
import { useI18n } from '../../i18n';
import SEO, { SEOConfigs } from '../common/SEO';

const Blog = () => {
  const { t } = useI18n();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let active = true;
    apiService.get('/blog', { limit: 24 })
      .then((response) => { if (active) setPosts(Array.isArray(response?.posts) ? response.posts : []); })
      .catch((error) => console.error('Error fetching blog posts:', error))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const filteredPosts = useMemo(() => posts.filter((post) => `${post.title} ${post.excerpt} ${post.category}`.toLowerCase().includes(search.toLowerCase())), [posts, search]);
  const fallbackImage = getImageUrl('/Images/1-The Art of Patience_WhatsApp Image 2024-08-12 at 19.03.36.jpeg');

  return (
    <div className="tafsir-page">
      <SEO {...SEOConfigs.blog} />
      <div className="page-shell">
        <header className="grid gap-8 border-b border-border pb-9 lg:grid-cols-[1fr_360px] lg:items-end">
          <div>
            <p className="editorial-eyebrow mb-4">{t('blog.eyebrow')}</p>
            <h1 className="editorial-title">{t('blog.title')}</h1>
            <p className="editorial-lede mt-5 max-w-2xl">{t('blog.subtitle')}</p>
          </div>
          <label className="flex h-12 items-center border-b border-text-primary">
            <MagnifyingGlass size={19} className="text-text-secondary" />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t('common.search')} className="min-w-0 flex-1 bg-transparent px-3 text-sm outline-none placeholder:text-text-muted" />
          </label>
        </header>

        {loading ? (
          <div className="grid gap-5 py-10 md:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((item) => <div key={item} className="skeleton-line h-[420px]" />)}</div>
        ) : filteredPosts.length === 0 ? (
          <div className="my-10 grid min-h-[320px] place-items-center border border-border bg-surface px-6 text-center">
            <div><p className="font-display text-3xl font-semibold">{t('blog.title')}</p><p className="mt-2 text-text-secondary">No journal entries yet.</p></div>
          </div>
        ) : (
          <section className="grid gap-x-5 gap-y-12 py-12 md:grid-cols-2 lg:grid-cols-3">
            {filteredPosts.map((post, index) => (
              <article key={post._id || post.slug} className={`group ${index === 0 ? 'md:col-span-2' : ''}`}>
                <Link to={`/blog/${post.slug || post._id}`}>
                  <div className={`overflow-hidden bg-surface ${index === 0 ? 'h-[420px]' : 'h-72'}`}>
                    <img src={post.featuredImage || fallbackImage} alt={post.title} className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.025]" />
                  </div>
                  <div className="border-t border-border py-4">
                    <span className="editorial-eyebrow block text-[10px]">{post.category || t('common.blog')}</span>
                    <div className="mt-2 flex items-start justify-between gap-4">
                      <h2 className={`font-display font-semibold leading-none ${index === 0 ? 'text-4xl' : 'text-2xl'}`}>{post.title}</h2>
                      <ArrowRight size={19} className="shrink-0 text-primary" />
                    </div>
                    {post.excerpt && <p className="mt-3 line-clamp-2 leading-7 text-text-secondary">{post.excerpt}</p>}
                  </div>
                </Link>
              </article>
            ))}
          </section>
        )}
      </div>
    </div>
  );
};

export default Blog;
